import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useGetNotificationsQuery, useGetUnreadCountQuery, useMarkAllReadMutation, useMarkReadMutation } from "../api";
import { buildNotificationFallbackRoute } from "../consts/deepLinks";
import type { Notification, NotificationCategory } from "../types";

export function useNotifications(isPanelOpen: boolean) {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language as "ru" | "kk" | "en";

  const [page, setPage] = useState(1);
  const [accumulated, setAccumulated] = useState<Notification[]>([]);
  const [activeCategory, setActiveCategory] = useState<NotificationCategory | null>(null);

  const { data: unreadData } = useGetUnreadCountQuery(undefined, {
    pollingInterval: isPanelOpen ? 0 : 60_000,
    refetchOnFocus: !isPanelOpen,
    skipPollingIfUnfocused: true,
  });

  const { data: notificationsData, isFetching } = useGetNotificationsQuery(
    { lang, page, category: activeCategory ?? undefined },
    { skip: !isPanelOpen },
  );

  const [markReadMutation] = useMarkReadMutation();
  const [markAllReadMutation, { isLoading: isMarkingAllRead }] = useMarkAllReadMutation();

  const currentPageNotifications = notificationsData?.results ?? [];
  const notifications = page === 1 ? currentPageNotifications : [...accumulated, ...currentPageNotifications];
  const unreadCount = unreadData?.count ?? 0;
  const hasNextPage = notificationsData?.next != null;

  function handleLoadMore() {
    if (isFetching || !hasNextPage) return;
    setAccumulated(notifications);
    setPage((prev) => prev + 1);
  }

  function handleCategoryChange(category: NotificationCategory | null) {
    if (category === activeCategory) return;
    setActiveCategory(category);
    setPage(1);
    setAccumulated([]);
  }

  const handleNotificationClick = useCallback(
    async (notification: Notification) => {
      if (!notification.is_read) {
        await markReadMutation({ id: notification.id, wasUnread: true });
      }
      navigate(buildNotificationFallbackRoute(notification));
    },
    [markReadMutation, navigate],
  );

  async function handleMarkAllRead() {
    if (isMarkingAllRead) return;
    await markAllReadMutation();
    setAccumulated((prev) => prev.map((n) => ({ ...n, is_read: true })));
  }

  return {
    unreadCount,
    notifications,
    isFetching,
    hasNextPage,
    isMarkingAllRead,
    activeCategory,
    handleCategoryChange,
    handleLoadMore,
    handleNotificationClick,
    handleMarkAllRead,
  };
}
